/* eslint-disable react-refresh/only-export-components */
// ApiClientContext.tsx
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import axios, { AxiosInstance, AxiosError } from 'axios';
import config from './config';
import { useSession } from './SessionContext';
import { IUserSession } from './interfaces/IUserSession';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import { useTermsModal } from './contexts/TermsModalContext';
import { applyRefreshToSession } from './utils/refreshSession';

interface ApiClientProviderProps {
    children: ReactNode;
}

type RetriableRequestConfig = NonNullable<AxiosError['config']> & {
    _retry?: boolean;
};

type TermsErrorPayload = {
    error?: string;
};

const PUBLIC_PATHS = ['/sign-in', '/sign-up'];

const ApiClientContext = createContext<{ [key: string]: AxiosInstance } | undefined>(undefined);

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({ children }) => {
    const { session, setSession } = useSession<IUserSession>();
    const token = session?.token;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { openTermsModal } = useTermsModal();
    const isPreviewMode = searchParams.get('preview') === 'true';

    const apiClients = useMemo(() => {
        const singleFlight = { promise: null as Promise<string> | null, token };
        const isCurrentSession = () => {
            try { return (JSON.parse(localStorage.getItem('session') ?? 'null') as IUserSession | null)?.token === singleFlight.token; }
            catch { return false; }
        };

        const refreshAccessToken = async (currentAccessToken?: string) => {
            if (singleFlight.promise) {
                return singleFlight.promise;
            }

            singleFlight.promise = (async () => {
                const refreshResp = await axios.post(
                    `${config.managementBaseUrl}/users/refresh`,
                    null,
                    {
                        withCredentials: true,
                        timeout: 10000,
                        headers: {
                            Authorization: `Bearer ${currentAccessToken ?? singleFlight.token}`
                        }
                    }
                );

                const newToken = refreshResp.data?.token;
                if (!newToken) {
                    throw new Error('Refresh response did not include a token');
                }

                if (!isCurrentSession())
                    throw new axios.CanceledError('Session changed during refresh');

                if (session) {
                    singleFlight.token = newToken;
                    setSession(applyRefreshToSession(session, refreshResp.data));
                }

                return newToken;
            })().finally(() => {
                singleFlight.promise = null;
            });

            return singleFlight.promise;
        };

        const createClient = (baseUrl: string) => {
            const client = axios.create({
                baseURL: baseUrl,
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Platform': 'web',
                },
                withCredentials: true
            });

            client.interceptors.request.use(
                (config) => {
                    // Don't send JWT in preview mode
                    if (singleFlight.token && !isPreviewMode) {
                        if (!isCurrentSession()) throw new axios.CanceledError('Session changed');
                        config.headers.Authorization = `Bearer ${singleFlight.token}`;
                    }
                    return config;
                },
                (error) => {
                    if (error.request) {
                        // The request was made but no response was received
                        console.error('No response received:', error.request);
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        console.error('Error', error.message);
                    }
                    return Promise.reject(error);
                }
            );

            client.interceptors.response.use(
                (res) => res,
                async (error: AxiosError) => {
                    const originalRequest = error.config as RetriableRequestConfig | undefined;
                    if (!originalRequest) {
                        return Promise.reject(error);
                    }
                    if (axios.isCancel(error) || (singleFlight.token && !isCurrentSession())) {
                        return Promise.reject(new axios.CanceledError('Session changed'));
                    }

                    const isRefreshRequest = originalRequest?.url?.includes('/users/refresh');

                    // Handle 403 - Terms acceptance required
                    if (error.response?.status === 403) {
                        const errorData = (error.response.data ?? {}) as TermsErrorPayload;
                        console.log('403 error received:', errorData);
                        console.log('Checking error field:', { field: errorData?.error, type: typeof errorData?.error });
                        console.log('String match?', errorData?.error === 'Terms acceptance required');
                        if (errorData?.error === 'Terms acceptance required') {
                            console.log('Terms acceptance required, opening modal');
                            // Fetch current terms versions from backend
                            try {
                                const termsStatusResp = await axios.get(
                                    `${config.managementBaseUrl}/users/me/terms-status`,
                                    {
                                        headers: {
                                            Authorization: `Bearer ${token}`
                                        }
                                    }
                                );

                                const { currentTermsVersion, currentSignupTermsVersion, isUpdate } = termsStatusResp.data;
                                console.log('Terms status fetched:', { currentTermsVersion, currentSignupTermsVersion, isUpdate });

                                // Return a promise that resolves when the modal acceptance completes
                                return new Promise((resolve, reject) => {
                                    console.log('Opening terms modal...');
                                    openTermsModal(
                                        currentTermsVersion,
                                        currentSignupTermsVersion,
                                        isUpdate,
                                        async () => {
                                            try {
                                                // Retry the original request
                                                const retryResponse = await client(originalRequest);
                                                resolve(retryResponse);
                                            } catch (retryError) {
                                                reject(retryError);
                                            }
                                        },
                                        () => {
                                            // Modal closed/cancelled - log user out
                                            console.log('Terms modal cancelled, logging out');
                                            setSession(null);
                                            navigate('/');
                                            reject(error);
                                        }
                                    );
                                });
                            } catch (termsStatusError) {
                                console.error('Failed to fetch terms status:', termsStatusError);
                                // Even if we can't fetch terms status (likely also 403), open modal with null values
                                // The modal will fetch terms internally or use defaults
                                return new Promise((resolve, reject) => {
                                    console.log('Opening terms modal with fallback (status fetch failed)...');
                                    openTermsModal(
                                        null, // currentTermsVersion - modal will handle null
                                        null, // currentSignupTermsVersion - modal will handle null
                                        false, // isUpdate
                                        async () => {
                                            try {
                                                // Retry the original request
                                                const retryResponse = await client(originalRequest);
                                                resolve(retryResponse);
                                            } catch (retryError) {
                                                reject(retryError);
                                            }
                                        },
                                        () => {
                                            // Modal closed/cancelled - log user out
                                            console.log('Terms modal cancelled, logging out');
                                            setSession(null);
                                            navigate('/');
                                            reject(error);
                                        }
                                    );
                                });
                            }
                        }
                    }

                    // Handle 401 - Token expired
                    if (
                        error.response?.status === 401 &&
                        !originalRequest._retry &&
                        !PUBLIC_PATHS.includes(location.pathname) &&
                        !isRefreshRequest
                    ) {
                        originalRequest._retry = true;
                        try {
                            const newToken = await refreshAccessToken(singleFlight.token);

                            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                            return client(originalRequest);
                        } catch (refreshErr: unknown) {
                            if (axios.isCancel(refreshErr) || !isCurrentSession()) return Promise.reject(refreshErr);
                            console.error('Refresh failed', refreshErr);
                            const status = refreshErr instanceof AxiosError ? refreshErr.response?.status : undefined;
                            const isTerminal = status === 400 || status === 401 || status === 403;
                            if (isTerminal) {
                                // Server confirmed the session is invalid — log out.
                                setSession(null);
                                navigate('/');
                            }
                            // Transient failure: reject the original request without logging out.
                            // The user keeps their session; they can retry when connectivity returns.
                        }
                    }

                    return Promise.reject(error);
                }
            );


            return client;
        };
        return {
            managementClient: createClient(config.managementBaseUrl),
            applicationClient: createClient(config.applicationBaseUrl)
        };
    }, [
        token,
        isPreviewMode,
        session,
        setSession,
        navigate,
        location.pathname,
        openTermsModal
    ]);

    return (
        <ApiClientContext.Provider value={apiClients}>
            {children}
        </ApiClientContext.Provider>
    );
};

export const useApiClient = (clientName: 'managementClient' | 'applicationClient') => {
    const context = useContext(ApiClientContext);
    if (!context) {
        throw new Error('useApiClient must be used within an ApiClientProvider');
    }
    return context[clientName];
};
